import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  InputLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { VideoPlayer } from '.';

const ProgressBar = ({ percentage }: { percentage: number }) => (
  <Box sx={{ width: '100%', height: 4, bgcolor: 'grey.300', borderRadius: 1 }}>
    <Box
      sx={{
        width: `${percentage}%`,
        height: 4,
        transition: 'width .3s linear',
        bgcolor: 'primary.dark',
      }}
    />
    <Typography fontSize="small" textAlign="right">
      {percentage}%
    </Typography>
  </Box>
);

const examples = {
  youtube: 'https://www.youtube.com/watch?v=LXb3EKWsInQ',
  vimeo: 'https://vimeo.com/76979871',
  mp4: 'https://www.w3schools.com/html/mov_bbb.mp4',
};

export const VideoPlayerExample = () => {
  const [url, setUrl] = useState('https://www.youtube.com/watch?v=LXb3EKWsInQ');
  const [percentage, setPercentage] = useState(0);
  const { meta } = useVideoMeta(url);

  return (
    <>
      <Stack
        direction={{ sm: 'column', md: 'row' }}
        position="relative"
        spacing={4}
      >
        <VideoPlayer url={url} onProgress={setPercentage} />
        <Stack
          sx={{
            width: { sm: '100%', md: 400 },
            bgcolor: 'neutral.50',
            borderRadius: 1,
            p: 3,
          }}
        >
          <Typography variant="h3" mt={4}>
            {meta?.title ?? 'Título do vídeo'}
          </Typography>
          <Box mt={2}>
            <Typography variant="body2">Assistido:</Typography>
            <ProgressBar percentage={percentage} />
          </Box>
          <Box mt={8}>
            <InputLabel>URL do vídeo</InputLabel>
            <TextField
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Cole o link do vídeo"
              fullWidth
            />
            <Typography display="block" textAlign="center" py={2}>
              ou
            </Typography>
            <Stack justifyContent="center" direction="row" spacing={1} mb={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setUrl(examples.youtube)}
              >
                YouTube
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setUrl(examples.vimeo)}
              >
                Vimeo
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setUrl(examples.mp4)}
              >
                MP4
              </Button>
            </Stack>
            <Link
              href="https://cookpete.github.io/react-player/"
              target="_blank"
            >
              Demo completa react-player
            </Link>
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

type Meta = { title?: string };
// Essas informações vão vir do backend, adicionei aqui só para o exemplo

const useVideoMeta = (url: string) => {
  const [meta, setMeta] = useState<Meta>({});
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!url) return;
    const controller = new AbortController();

    const oembed = url.includes('vimeo')
      ? `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`
      : `https://noembed.com/embed?url=${encodeURIComponent(url)}`;

    fetch(oembed, { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => setMeta({ title: data.title }))
      .catch((err) => !controller.signal.aborted && setError(err.message));

    return () => controller.abort();
  }, [url]);

  return { meta, error };
};
